import { useEffect, useSyncExternalStore } from "react";
import { api } from "./api";

/**
 * One shared, always-fresh copy of the subscription plans (GET /pricing/plans, never cached by the server).
 * Landing, Subscription, checkout and the dashboard all read it, so an admin edit shows up everywhere.
 *  - refetches when the tab regains focus / becomes visible, and when notifyPlansUpdated() is called
 *  - a response that arrives late can never overwrite a newer one (sequence guard)
 */
let state = { plans: null, loading: false, error: null, loadedAt: 0 };
let seq = 0;
const listeners = new Set();
const emit = () => listeners.forEach((l) => l());

let retryTimer = null;
let attempts = 0;

export async function refreshPlans() {
  const mine = ++seq;
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
  state = { ...state, loading: true };
  emit();
  try {
    const { data } = await api.get("/pricing/plans", { params: { _: Date.now() }, timeout: 30000 });
    if (mine !== seq) return state.plans; // a newer request is in flight; drop this stale answer
    attempts = 0;
    state = { plans: Array.isArray(data) ? data : [], loading: false, error: null, loadedAt: Date.now() };
  } catch (e) {
    if (mine !== seq) return state.plans;
    // keep the last good plans on screen; try again by itself (2 s, 4 s, 8 s ... max 30 s) so one slow or dropped request never leaves the page empty
    state = { ...state, loading: false, error: e?.response?.data?.detail || "Could not refresh plans" };
    attempts += 1;
    if (typeof window !== "undefined") retryTimer = setTimeout(() => { retryTimer = null; refreshPlans(); }, Math.min(30000, 1000 * 2 ** Math.min(attempts, 5)));
  }
  emit();
  return state.plans;
}

/** Call after an admin saves a plan, price or the plan/service matrix. */
export const notifyPlansUpdated = () => refreshPlans();

let wired = false;
function wire() {
  if (wired || typeof document === "undefined") return;
  wired = true;
  const again = () => { if (document.visibilityState === "visible" && Date.now() - state.loadedAt > 15000) refreshPlans(); };
  document.addEventListener("visibilitychange", again);
  window.addEventListener("focus", again);
}

const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
const snapshot = () => state;

/** { plans, loading, error, refresh }. `plans` is null until the first answer arrives. */
export function usePlans() {
  const s = useSyncExternalStore(subscribe, snapshot, snapshot);
  useEffect(() => { wire(); if (!s.plans && !s.loading) refreshPlans(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return { plans: s.plans, loading: s.loading, error: s.error, refresh: refreshPlans };
}

/** Prefer the live plans; fall back to a content snapshot only until the first answer arrives. */
export const mergeLivePlans = (live, fallback) => (Array.isArray(live) && live.length ? live : (fallback || []));
